import { CommonModule, DatePipe } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  OnInit,
  QueryList,
  signal,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { _, TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectButtonModule } from 'primeng/selectbutton';
import { Table, TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import {
  Inspection,
  InspectionItem,
  InspectionItemMetadata,
  InspectionMetadata,
  StoreInspectionSchema,
  UpdateInspectionSchema,
} from '../../shared/models/inspection.model';
import { RouteSection } from '../../shared/models/route.model';
import { InspectionService } from '../../shared/services/inspection.service';
import { InspectionItemTableComponent } from './components/inspection-item-table/inspection-item-table.component';

@Component({
  selector: 'app-inspections-page',
  imports: [
    CommonModule,
    CardModule,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    ToastModule,
    TableModule,
    ButtonModule,
    DatePickerModule,
    IconFieldModule,
    InputTextModule,
    DatePipe,
    ConfirmDialogModule,
    ProgressBarModule,
    TagModule,
    MultiSelectModule,
    InspectionItemTableComponent,
    DialogModule,
    SelectButtonModule,
  ],
  providers: [
    MessageService,
    InspectionService,
    ConfirmationService,
    TranslateService,
    ReactiveFormsModule,
  ],
  templateUrl: './inspections-page.component.html',
  styleUrl: './inspections-page.component.scss',
})
export class InspectionsPageComponent implements OnInit {
  @ViewChild('inspectionsTable') inspectionTable!: Table;
  @ViewChildren(InspectionItemTableComponent)
  inspectionItemTables!: QueryList<InspectionItemTableComponent>;
  protected readonly Math = Math;
  #messageService = inject(MessageService);
  #confirmationService = inject(ConfirmationService);
  #translateService = inject(TranslateService);
  #router = inject(Router);
  #activatedRoute = inject(ActivatedRoute);
  inspectionService = inject(InspectionService);

  form: FormGroup;
  popupVisible = false;

  selectedInspection = signal<Inspection | null>(null);
  // Inspektionshistorie: nur Inspektionen anzeigen, die dieses Element enthalten
  modelFilter = signal<{ type: string; id: string; name: string } | null>(
    null,
  );
  invalidFieldsMap = signal<Map<string | number, Set<string>>>(new Map());

  // Map für alle Inspections die gerade bearbeitet werden (Key = ID, Value = geklonte Inspection)
  editingInspections = signal<Map<string | number, Inspection>>(new Map());

  public globalFilterFields = InspectionMetadata.globalFilterFields;
  public inspectionColumns = InspectionMetadata.inspectionColumns;
  public inspectionItemColumns = InspectionItemMetadata.inspectionItemColumns;
  protected readonly round = Math.round;

  public displayInspections = computed(() => {
    const original = this.inspectionService.inspections();
    const editing = this.editingInspections();

    // Neue Rows (mit temp_ IDs)
    const newRows = Array.from(editing.entries())
      .filter(([id]) => String(id).startsWith('temp_'))
      .map(([_, inspection]) => inspection);

    const modelFilter = this.modelFilter();
    const filtered = modelFilter
      ? original.filter((inspection) =>
          inspection.models?.some(
            (m) =>
              m.model_type === modelFilter.type &&
              String(m.model_id) === modelFilter.id,
          ),
        )
      : original;

    // Existierende Rows (bearbeitete Version wenn vorhanden, sonst Original)
    const existingRows = filtered.map(
      (inspection) => editing.get(inspection.id) || inspection,
    );

    return [...newRows, ...existingRows];
  });

  itemTypeOptions = [
    {
      key: 'route',
      value: this.#translateService.instant('inspection.routes'),
    },
    {
      key: 'psa',
      value: this.#translateService.instant('inspection.harnesses'),
    },
  ];

  // Options mit disabled-Flag fuer bereits gecheckte Items
  itemOptions = computed(() => {
    const inspection = this.selectedInspection();
    const availableItems = this.inspectionService.inspectionItems();
    const checkedItems = inspection?.models?.filter(
      (item: InspectionItem) => item.checked_at !== null,
    );

    if (!checkedItems?.length) {
      return availableItems.map((item: any) => ({ ...item, disabled: false }));
    }

    const checkedModelIds = checkedItems.map(
      (item: InspectionItem) => item.model_id,
    );
    const itemType = this.form.value.itemType;

    return availableItems.map((item: any) => {
      let isLocked = false;
      if (itemType === 'route') {
        isLocked = item.sections?.some((section: RouteSection) =>
          checkedModelIds.includes(section.id),
        );
      } else {
        isLocked = checkedModelIds.includes(item.id);
      }
      return { ...item, disabled: isLocked };
    });
  });

  constructor(private fb: FormBuilder) {
    this.form = this.fb.group({
      itemType: ['route'],
      items: [[] as InspectionItem[]],
    });

    effect(() => {
      const inspection = this.selectedInspection();
      const items = inspection?.models;
      if (items && items.length > 0) {
        const modelType = items[0].model_type;
        const itemType =
          modelType === 'route_section' ? 'route' : modelType ?? 'route';
        this.form.patchValue({ itemType });
        this.inspectionService.getPotentialInspectionItems(itemType);
      }
    });
  }

  #prefillEffect = effect(() => {
    const inspection = this.selectedInspection();
    const options = this.itemOptions();
    if (!inspection?.models?.length || !options.length) return;

    const itemType = this.form.value.itemType;
    const assignedModelIds = inspection.models.map(
      (item: InspectionItem) => item.model_id,
    );

    if (itemType === 'route') {
      const selectedRoutes = options.filter((route: any) =>
        route.sections?.some((section: RouteSection) =>
          assignedModelIds.includes(section.id),
        ),
      );
      this.form.patchValue({ items: selectedRoutes });
    } else if (itemType === 'psa') {
      const selectedPsas = options.filter((psa: any) =>
        assignedModelIds.includes(psa.id),
      );
      this.form.patchValue({ items: selectedPsas });
    }
  });

  ngOnInit(): void {
    const params = this.#activatedRoute.snapshot.queryParamMap;
    const modelType = params.get('modelType');
    const modelId = params.get('modelId');
    if (modelType && modelId) {
      this.modelFilter.set({
        type: modelType,
        id: modelId,
        name: params.get('modelName') ?? modelId,
      });
    }

    this.inspectionService.getInspections();
  }

  clearModelFilter() {
    this.modelFilter.set(null);
    this.#router.navigate([], {
      relativeTo: this.#activatedRoute,
      queryParams: {},
    });
  }

  manageModels(inspection: Inspection) {
    this.selectedInspection.set(inspection);
    this.popupVisible = true;
    this.inspectionService.getPotentialInspectionItems(
      this.form.value.itemType ?? 'route',
    );
  }
  onRowCreateInit() {
    const tempId = `temp_${Date.now()}_${Math.random()}`;
    const newRow: Partial<Inspection> = {
      id: tempId as any,
      name: '',
      date: undefined,
    };

    this.editingInspections.update((map) => {
      const newMap = new Map(map);
      newMap.set(tempId, newRow as Inspection);
      return newMap;
    });

    if (this.inspectionTable) {
      this.inspectionTable.initRowEdit(newRow);
    }
  }
  onRowEditInit(inspection: Inspection) {
    this.invalidFieldsMap.update((map) => {
      const newMap = new Map(map);
      newMap.delete(inspection.id);
      return newMap;
    });
    this.editingInspections.update((map) => {
      const newMap = new Map(map);
      newMap.set(inspection.id, {
        ...inspection,
        date: inspection.date ? new Date(inspection.date) : inspection.date,
      } as Inspection);
      return newMap;
    });
  }
  onRowEditSave(inspection: Inspection) {
    const isNew = String(inspection.id).startsWith('temp_');

    const data = { name: inspection.name, date: inspection.date };
    const schema = isNew ? StoreInspectionSchema : UpdateInspectionSchema;
    const result = schema.safeParse(data);

    if (!result.success) {
      const fields = new Set(
        result.error.issues.map((i) => i.path[0]?.toString()).filter(Boolean),
      );
      this.invalidFieldsMap.update((map) => {
        const newMap = new Map(map);
        newMap.set(inspection.id, fields as Set<string>);
        return newMap;
      });
      this.#messageService.add({
        severity: 'warn',
        summary: this.#translateService.instant('label.errorHeader'),
        detail: this.#translateService.instant(
          result.error.issues[0]?.code === 'too_small'
            ? 'validation.required'
            : 'validation.invalid',
        ),
      });
      return;
    }

    this.invalidFieldsMap.update((map) => {
      const newMap = new Map(map);
      newMap.delete(inspection.id);
      return newMap;
    });

    if (isNew) {
      // Create new inspection
      const newInspection = { ...inspection, id: null };
      this.inspectionService.createInspection(newInspection).subscribe({
        next: () => {
          delete this.inspectionTable.editingRowKeys[String(inspection.id)];
          this.editingInspections.update((map) => {
            const newMap = new Map(map);
            newMap.delete(inspection.id);
            return newMap;
          });

          this.inspectionService.getInspections();

          this.#messageService.add({
            severity: 'success',
            summary: this.#translateService.instant('label.successHeader'),
            detail: this.#translateService.instant('inspection.created'),
          });
        },
        error: (e: any) => {
          this.#messageService.add({
            severity: 'error',
            summary: this.#translateService.instant('label.errorHeader'),
            detail: this.#translateService.instant('inspection.createError'),
          });
        },
      });
    } else {
      // Update existing inspection
      this.inspectionService
        .updateInspection(inspection, inspection.id)
        .subscribe({
          next: () => {
            delete this.inspectionTable.editingRowKeys[String(inspection.id)];
            this.editingInspections.update((map) => {
              const newMap = new Map(map);
              newMap.delete(inspection.id);
              return newMap;
            });

            this.inspectionService.getInspections();

            this.#messageService.add({
              severity: 'success',
              summary: this.#translateService.instant('label.successHeader'),
              detail: this.#translateService.instant('inspection.updated'),
            });
          },
          error: (e: any) => {
            this.#messageService.add({
              severity: 'error',
              summary: this.#translateService.instant('label.errorHeader'),
              detail: this.#translateService.instant('inspection.updateError'),
            });
          },
        });
    }
  }

  onRowEditCancel(inspection: Inspection) {
    this.invalidFieldsMap.update((map) => {
      const newMap = new Map(map);
      newMap.delete(inspection.id);
      return newMap;
    });
    this.editingInspections.update((map) => {
      const newMap = new Map(map);
      newMap.delete(inspection.id);
      return newMap;
    });
  }

  onRowDeleteInit(event: Event, inspection: Inspection) {
    this.#confirmationService.confirm({
      target: event.target as EventTarget,
      message: this.#translateService.instant(
        'inspection.deleteConfirmMessage',
      ),
      header: this.#translateService.instant('inspection.deleteHeader'),
      rejectLabel: this.#translateService.instant('label.cancel'),
      rejectButtonProps: {
        label: this.#translateService.instant('label.cancel'),
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: this.#translateService.instant('label.delete'),
        severity: 'danger',
      },

      accept: () => {
        this.inspectionService.deleteInspection(inspection.id).subscribe({
          next: () => {
            this.inspectionService.getInspections();
            this.#messageService.add({
              severity: 'success',
              summary: this.#translateService.instant(
                'label.successfullyDeletedHeader',
              ),
              detail: this.#translateService.instant(
                'label.successfullyDeleted',
              ),
            });
          },
          error: (e: any) => {
            this.#messageService.add({
              severity: 'error',
              summary: this.#translateService.instant('label.errorHeader'),
              detail: this.#translateService.instant('label.errorDeleting'),
            });
          },
        });
      },
      reject: () => {
        //nothing
      },
    });
  }

  manageItems() {
    this.popupVisible = true;
  }

  onSubmit() {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    const inspection = this.selectedInspection();

    if (!inspection?.id) {
      return;
    }

    const itemType = this.form.value.itemType;
    const selectedItems = this.form.value.items ?? [];

    const payload: Record<string, unknown> =
      itemType === 'route'
        ? { route_ids: selectedItems.map((item: any) => item.id) }
        : { psa_ids: selectedItems.map((item: any) => item.id) };

    this.inspectionService
      .updateInspection(payload, inspection.id)
      .subscribe({
        next: () => {
          this.closePopup();
          this.inspectionService.getInspections();

          this.#messageService.add({
            severity: 'success',
            summary: this.#translateService.instant('label.successHeader'),
            detail: this.#translateService.instant('inspection.updated'),
          });
        },
        error: (e: any) => {
          this.#messageService.add({
            severity: 'error',
            summary: this.#translateService.instant('label.errorHeader'),
            detail: this.#translateService.instant('inspection.updateError'),
          });
        },
      });
  }

  selectOverduePsas() {
    this.inspectionService.getOverduePsas().subscribe({
      next: (overduePsas: any) => {
        this.form.patchValue({ items: overduePsas });
      },
    });
  }

  closePopup() {
    this.popupVisible = false;
    this.selectedInspection.set(null);
    this.form.reset({ itemType: 'route', items: [] });
  }

  changeItemOptions() {
    const selectedItemType = this.form.value.itemType;
    this.inspectionService.getPotentialInspectionItems(selectedItemType);
  }

  onItemChecked() {
    this.inspectionService.getInspections();
  }
}

export const inspectionPageTranslations = _([
  'inspection.routes',
  'inspection.harnesses',
]);
